import { usePostStatusReset } from "@/api/areas/mutations";
import { Area, PostAction, Status } from "@/api/areas/types";
import { Pause, WrenchIcon } from "lucide-react";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import tw, { styled } from "twin.macro";
import { LoadingSpinner } from "./LoadingIndicator";
import AreaActionModal from "./modals/AreaActionModal";

export type ButtonProps = {
  indigo?: boolean;
  cyan?: boolean;
  green?: boolean;
  red?: boolean;
  orange?: boolean;
  yellow?: boolean;
  white?: boolean;
  onprimary?: boolean;
  primary?: boolean;
  secondary?: boolean;
  repair?: boolean;
  warning?: boolean;
};

export const buttonStyle = {
  base: tw`transition flex items-center justify-center gap-1.5 rounded-b-xl rounded-tl-xl px-4 py-1.5
  text-center text-sm font-semibold
  border border-transparent
  focus:(ring-2 ring-offset-2 outline-none)
  disabled:(opacity-70 cursor-not-allowed)`,
  indigo: tw`shadow-sm bg-indigo-600 text-white hover:bg-indigo-700 focus:(ring-indigo-600)`,
  cyan: tw`shadow-sm bg-cyan-600 text-white hover:bg-cyan-700 focus:(ring-cyan-600)`,
  green: tw`shadow-sm bg-emerald-600 text-white hover:bg-emerald-700 focus:(ring-emerald-600)`,
  red: tw`shadow-sm bg-red-600 text-white hover:bg-red-700 focus:(ring-red-600)`,
  orange: tw`shadow-sm bg-orange-600 text-white hover:bg-orange-700 focus:(ring-orange-600)`,
  yellow: tw`shadow-sm bg-yellow-500 text-black hover:bg-yellow-600 focus:(ring-yellow-500)`,
  white: tw`bg-white border-gray-300 text-gray-700 hover:bg-gray-100 focus:(ring-gray-500)`,
  onPrimary: tw`bg-white border-primary-800 text-primary-800 hover:bg-primary-100 focus:(ring-primary-800)`,
  primary: tw`shadow-sm bg-primary-800 text-white hover:bg-primary-900 focus:(ring-primary-800)`,
  secondary: tw`shadow-sm bg-secondary-600 text-white hover:bg-secondary-700 focus:(ring-secondary-600)`,
  repair: tw`shadow-sm bg-transparent text-gray-700 border-gray-500 hover:bg-gray-100 focus:(ring-gray-500)`,
  warning: tw`shadow-sm bg-transparent text-orange-700 border-orange-700 hover:bg-orange-200 focus:(ring-orange-400)`,
};

export const Button = styled.button<ButtonProps>((props) => [
  buttonStyle.base,
  props.indigo && buttonStyle.indigo,
  props.cyan && buttonStyle.cyan,
  props.green && buttonStyle.green,
  props.red && buttonStyle.red,
  props.orange && buttonStyle.orange,
  props.yellow && buttonStyle.yellow,
  props.white && buttonStyle.white,
  props.primary && buttonStyle.primary,
  props.onprimary && buttonStyle.onPrimary,
  props.secondary && buttonStyle.secondary,
  props.repair && buttonStyle.repair,
  props.warning && buttonStyle.warning,
]);

// XXX: Maybe use ChevronsLeftRight & ChevronsRightLeft for open and close (fold/unfold) actions.
// Writing the whole word could take up too much space on each line with the battery and pause indicators.

const ButtonsContainer = tw.div`flex gap-3 flex-row items-center`;

export const DeployAllOnlyUPButton = ({
  areaId,
  status,
}: {
  areaId: string;
  status: Status;
}) => {
  const { t } = useTranslation();
  const [openActionModal, setopenActionModal] = useState<boolean>(false);
  const [action, setAction] = useState<PostAction>();

  const handleDeploy = async (action: PostAction) => {
    setAction(action);
    setopenActionModal(true);
  };

  //#region : Action buttons
  const pauseButton = (
    <Button
      onClick={() => handleDeploy("PAUSE")}
      white
      tw="text-stone-500 border-stone-500 cursor-pointer p-1"
    >
      <Pause tw="fill-stone-500 h-4 " />
    </Button>
  );

  const closeButton = (
    <Button primary onClick={() => handleDeploy("CLOSE")}>
      {t("action.foldAll")}
    </Button>
  );

  const openButton = (
    <Button secondary onClick={() => handleDeploy("OPEN")}>
      {t("action.deployAll")}
    </Button>
  );
  //#endregion : Action buttons

  const buttonsForStatusMap: Record<Status, ReactNode> = {
    pending: (
      <Fragment>
        <LoadingSpinner tw="text-primary-800 h-6" />
        {pauseButton}
      </Fragment>
    ),
    closed: openButton,
    paused: null,
    opened: closeButton,
    problem: null,
    up: null, // This should not be a possible state
  };

  return (
    <ButtonsContainer>
      {buttonsForStatusMap[status] || openButton}

      {action && (
        <AreaActionModal
          areaId={areaId}
          action={action}
          open={openActionModal}
          onClose={() => setopenActionModal(false)}
        />
      )}
    </ButtonsContainer>
  );
};

export const RepairButton = ({
  postName,
  areaId,
}: {
  postName: string;
  areaId: Area["_id"];
  status: string;
}) => {
  const { mutateAsync: repairPost } = usePostStatusReset(areaId);

  const handleResetPostStatus = async () => {
    try {
      await repairPost({ postName });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Button
      repair
      tw="cursor-pointer p-1"
      onClick={() => handleResetPostStatus()}
    >
      <WrenchIcon tw="h-5" />
    </Button>
  );
};
